import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Hash,
  FileText,
  CalendarCheck,
  CalendarX,
  Boxes,
} from 'lucide-react';
import { API_BASE_URL } from '@/lib/utils/constants';
import { cn } from '@/lib/utils/cn';

type PublicLineItem = {
  id: string;
  quantity: number;
  totalPrice: number;
  material: { name: string; sku: string | null };
};

type PublicQuote = {
  code: string;
  clientName: string;
  status:
    | 'DRAFT'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'
    | 'ADJUSTMENT_REQUESTED'
    | 'EXPIRED'
    | 'CANCELLED';
  finalPrice: number;
  validUntil: string;
  createdAt: string;
  currency: { code: string; symbol: string };
  tenant: { name: string; logoUrl: string | null };
  lineItems: PublicLineItem[];
};

const STATUS_LABEL: Record<PublicQuote['status'], string> = {
  DRAFT: 'En preparación',
  PENDING_APPROVAL: 'Pendiente de aprobación',
  APPROVED: '¡Aprobada!',
  REJECTED: 'Rechazada',
  ADJUSTMENT_REQUESTED: 'En ajuste',
  EXPIRED: 'Expirada',
  CANCELLED: 'Cancelada',
};

const STATUS_STYLE: Record<
  PublicQuote['status'],
  { color: string; Icon: typeof CheckCircle2; helper: string }
> = {
  DRAFT: {
    color: 'bg-slate-100 text-slate-700 border-slate-200',
    Icon: Clock,
    helper: 'Estamos preparando tu propuesta.',
  },
  PENDING_APPROVAL: {
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    Icon: Clock,
    helper: 'Tu propuesta está siendo revisada internamente.',
  },
  APPROVED: {
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Icon: CheckCircle2,
    helper: '¡Listo! La propuesta fue aprobada y está en firme.',
  },
  REJECTED: {
    color: 'bg-red-50 text-red-700 border-red-200',
    Icon: XCircle,
    helper: 'Esta versión no avanzó. Contactá a tu representante.',
  },
  ADJUSTMENT_REQUESTED: {
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    Icon: Clock,
    helper: 'Estamos haciendo ajustes a la propuesta.',
  },
  EXPIRED: {
    color: 'bg-slate-100 text-slate-500 border-slate-200',
    Icon: XCircle,
    helper: 'La validez de esta propuesta ya venció. Pedí una nueva.',
  },
  CANCELLED: {
    color: 'bg-red-50 text-red-700 border-red-200',
    Icon: XCircle,
    helper: 'Esta propuesta fue cancelada.',
  },
};

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-BO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatMoney(v: number, currency = 'USD'): string {
  return `${currency} ${v.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function daysFromNow(d: string | null | undefined): number | null {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function relativeLabel(days: number | null): string | null {
  if (days == null) return null;
  if (days === 0) return 'vence hoy';
  if (days === 1) return 'vence mañana';
  if (days === -1) return 'venció ayer';
  if (days > 0) return `vence en ${days} días`;
  return `venció hace ${Math.abs(days)} días`;
}

export function PublicQuoteTrackingPage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/public/track/quote/${token}`)
      .then((res) => {
        setQuote(res.data?.data ?? null);
        setError(null);
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.response?.data?.message;
        setError(msg || 'No pudimos cargar la cotización. Verificá el link.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const totalQty = useMemo(
    () => (quote ? quote.lineItems.reduce((s, it) => s + (Number(it.quantity) || 0), 0) : 0),
    [quote]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Cargando cotización…</div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertCircle className="w-12 h-12 mx-auto text-amber-500" />
          <h1 className="text-xl font-semibold text-slate-800">No encontramos esta cotización</h1>
          <p className="text-sm text-slate-600">
            {error ||
              'El link puede estar incompleto o haber expirado. Pedile uno nuevo a tu contacto.'}
          </p>
        </div>
      </div>
    );
  }

  const validityDays = daysFromNow(quote.validUntil);
  const expired = validityDays != null && validityDays < 0;
  const style = STATUS_STYLE[quote.status];
  const StatusIcon = style.Icon;
  const currency = quote.currency?.code ?? 'USD';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          {quote.tenant.logoUrl ? (
            <img src={quote.tenant.logoUrl} alt={quote.tenant.name} className="h-10 w-auto" />
          ) : (
            <img src="/prixia-icon.svg" alt="Prixia" className="h-10 w-10" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">Cotización</p>
            <p className="font-semibold text-slate-800 truncate">{quote.tenant.name}</p>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500">
            <div className="flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" />
              <span className="font-mono">{quote.code}</span>
            </div>
            <span className="text-slate-300">|</span>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">powered by</span>
              <img src="/prixia-logo.svg" alt="Prixia" className="h-4 w-auto" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Hero / resumen */}
        <section className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          <div className="p-5 sm:p-6 grid sm:grid-cols-[1fr_auto] gap-4 sm:items-center">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border',
                    style.color
                  )}
                >
                  <StatusIcon className="w-3.5 h-3.5" />
                  {STATUS_LABEL[quote.status]}
                </span>
                <span className="text-xs text-slate-400">·</span>
                <span className="text-xs text-slate-500">
                  Emitida el {formatDate(quote.createdAt)}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                {quote.code}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Propuesta para{' '}
                <span className="font-semibold text-slate-800">{quote.clientName}</span>
              </p>
              <p className="text-xs text-slate-500 mt-2 max-w-md">{style.helper}</p>
            </div>
            <div className="sm:text-right">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Monto total</p>
              <p className="text-3xl font-bold text-emerald-700 font-mono leading-tight">
                {formatMoney(quote.finalPrice, currency)}
              </p>
              <p
                className={cn(
                  'mt-1 inline-flex items-center gap-1 text-xs font-medium',
                  expired ? 'text-red-600' : 'text-slate-500'
                )}
              >
                {expired ? (
                  <CalendarX className="w-3.5 h-3.5" />
                ) : (
                  <CalendarCheck className="w-3.5 h-3.5" />
                )}
                {expired ? 'Validez vencida' : 'Válida hasta'} {formatDate(quote.validUntil)}
                {relativeLabel(validityDays) && (
                  <span className="text-slate-400 ml-1">· {relativeLabel(validityDays)}</span>
                )}
              </p>
            </div>
          </div>
        </section>

        {/* Resumen rápido */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">Ítems</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{quote.lineItems.length}</p>
          </div>
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">Unidades</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 font-mono">
              {totalQty.toLocaleString('es-BO')}
            </p>
          </div>
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">Moneda</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{currency}</p>
          </div>
        </section>

        {/* Detalle */}
        {quote.lineItems.length > 0 && (
          <section className="bg-white rounded-2xl border shadow-sm p-5 sm:p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-4 flex items-center gap-2">
              <Boxes className="w-4 h-4" />
              Detalle de la propuesta
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                    <th className="py-2 pr-2 font-semibold">Producto</th>
                    <th className="py-2 px-2 font-semibold text-right">Cantidad</th>
                    <th className="py-2 pl-2 font-semibold text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quote.lineItems.map((it) => (
                    <tr key={it.id} className="hover:bg-slate-50/60">
                      <td className="py-3 pr-2">
                        <p className="text-slate-800 font-medium">{it.material.name}</p>
                        {it.material.sku && (
                          <p className="text-xs text-slate-400 mt-0.5">SKU {it.material.sku}</p>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-slate-700">
                        {Number(it.quantity).toLocaleString('es-BO')}
                      </td>
                      <td className="py-3 pl-2 text-right font-mono font-semibold text-slate-900">
                        {formatMoney(it.totalPrice, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200">
                    <td
                      colSpan={2}
                      className="py-3 text-right text-sm font-semibold text-slate-700"
                    >
                      Total propuesta
                    </td>
                    <td className="py-3 pl-2 text-right font-mono font-bold text-emerald-700 text-base">
                      {formatMoney(quote.finalPrice, currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        )}

        {/* Card de "qué sigue" según estado */}
        {(quote.status === 'PENDING_APPROVAL' || quote.status === 'ADJUSTMENT_REQUESTED') && (
          <section className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-blue-700 font-semibold">
                Próximo paso
              </p>
              <p className="text-sm text-slate-800 mt-0.5">{style.helper}</p>
            </div>
          </section>
        )}

        {quote.status === 'APPROVED' && (
          <section className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                ¡Aprobada!
              </p>
              <p className="text-sm text-slate-800 mt-0.5">
                Tu cotización está confirmada. Tu representante se va a contactar para los próximos
                pasos.
              </p>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center text-xs text-slate-400 py-4 space-y-1">
          <p>
            Cotización compartida por{' '}
            <span className="font-semibold text-slate-500">{quote.tenant.name}</span> · powered by
            Prixia
          </p>
          <p className="text-[10px] text-slate-400">
            Este link es único para tu cotización. Para cualquier consulta, contactá a tu
            representante.
          </p>
        </footer>
      </main>
    </div>
  );
}
